import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useState, useEffect, useMemo } from 'react';
import { Info, Search, CheckSquare2, SquareX } from 'lucide-react';

interface Actividad {
  id: number;
  nombre: string;
}

interface AsignarActividadesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actividades: Actividad[];
  actividadesActualesIds: number[];
  onConfirm: (actividadesIds: number[], modo: 'replace' | 'add') => void;
  isLoading?: boolean;
}

export function AsignarActividadesModal({
  open,
  onOpenChange,
  actividades,
  actividadesActualesIds,
  onConfirm,
  isLoading = false,
}: AsignarActividadesModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [modo, setModo] = useState<'replace' | 'add'>('replace');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(actividadesActualesIds));
      setModo('replace');
      setSearch('');
    }
  }, [open, actividadesActualesIds]);

  const actividadesFiltradas = useMemo(() => {
    if (!search.trim()) {
      return actividades;
    }
    const searchTerm = search.toLowerCase();
    return actividades.filter((actividad) =>
      actividad.nombre.toLowerCase().includes(searchTerm)
    );
  }, [actividades, search]);

  // Reset selection when modal opens with new actividades actuales
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      onOpenChange(false);
      return;
    }
    setSelectedIds(new Set(actividadesActualesIds));
    setModo('replace');
    setSearch('');
    onOpenChange(true);
  };

  const toggleActividad = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const seleccionarTodas = () => {
    const idsFiltradas = actividadesFiltradas.map((a) => a.id);
    setSelectedIds(new Set([...selectedIds, ...idsFiltradas]));
  };

  const quitarTodas = () => {
    const idsFiltradas = new Set(actividadesFiltradas.map((a) => a.id));
    const nuevasSeleccionadas = new Set(
      Array.from(selectedIds).filter((id) => !idsFiltradas.has(id))
    );
    setSelectedIds(nuevasSeleccionadas);
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedIds), modo);
    onOpenChange(false);
  };

  const isDisabled = isLoading || selectedIds.size === 0;

  // Calculate changes for preview
  const nuevasActividades = Array.from(selectedIds).filter(id => !actividadesActualesIds.includes(id));
  const actividadesARemover = actividadesActualesIds.filter(id => !selectedIds.has(id));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>Asignar Actividades</DialogTitle>
          <DialogDescription>
            Selecciona las actividades para los aprendices seleccionados.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 max-h-[calc(80vh-120px)] overflow-y-auto">
          {/* Modo de asignación */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Modo de asignación</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setModo('replace')}
                className={`flex flex-col items-start p-3 rounded-lg border-2 transition-all ${
                  modo === 'replace'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    modo === 'replace' ? 'border-primary' : 'border-muted-foreground'
                  }`}>
                    {modo === 'replace' && <div className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                  <span className="text-sm font-medium">Reemplazar</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 ml-6">
                  Las actividades seleccionadas reemplazarán las asignaciones actuales
                </p>
              </button>
              <button
                onClick={() => setModo('add')}
                className={`flex flex-col items-start p-3 rounded-lg border-2 transition-all ${
                  modo === 'add'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    modo === 'add' ? 'border-primary' : 'border-muted-foreground'
                  }`}>
                    {modo === 'add' && <div className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                  <span className="text-sm font-medium">Agregar</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 ml-6">
                  Las actividades seleccionadas se sumarán a las ya asignadas
                </p>
              </button>
            </div>
          </div>

          {/* Barra de herramientas: búsqueda y selección masiva */}
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Actividades</Label>
              
              {/* Buscador */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  aria-label="Buscar actividades"
                />
                {search && (
                  <span className="text-xs text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2">
                    {actividadesFiltradas.length} coincidencias
                  </span>
                )}
              </div>

              {/* Botones de selección masiva */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={seleccionarTodas}
                  disabled={actividadesFiltradas.length === 0}
                  className="flex-1"
                  aria-label="Seleccionar todas las actividades filtradas"
                >
                  <CheckSquare2 className="h-4 w-4 mr-2" />
                  Seleccionar todas
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={quitarTodas}
                  disabled={actividadesFiltradas.length === 0}
                  className="flex-1"
                  aria-label="Quitar la selección de todas las actividades filtradas"
                >
                  <SquareX className="h-4 w-4 mr-2" />
                  Quitar todas
                </Button>
              </div>
            </div>

            {/* Lista de actividades */}
            <div className="space-y-2 min-h-[200px] max-h-[50vh] overflow-y-auto">
              {actividadesFiltradas.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Search className="h-12 w-12 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {search
                      ? 'No se encontraron actividades con ese criterio'
                      : 'No hay actividades disponibles'}
                  </p>
                </div>
              ) : (
                actividadesFiltradas.map((actividad) => {
                  const isAsignadaActualmente = actividadesActualesIds.includes(actividad.id);
                  const isSelected = selectedIds.has(actividad.id);

                  return (
                    <div
                      key={actividad.id}
                      className={`flex items-center space-x-3 rounded-lg border p-3 transition-all cursor-pointer hover:bg-muted/50 ${
                        isSelected ? 'bg-primary/5 border-primary/50' : ''
                      }`}
                      onClick={() => toggleActividad(actividad.id)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleActividad(actividad.id)}
                        aria-label={`Seleccionar ${actividad.nombre}`}
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium">{actividad.nombre}</span>
                        {isAsignadaActualmente && !isSelected && modo === 'replace' && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            Se desasignará
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Resumen de cambios */}
          {selectedIds.size > 0 && (
            <div className="rounded-md bg-muted/50 p-3 space-y-2 sticky bottom-0">
              <div className="flex items-start gap-2 text-sm">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="font-medium">Resumen de cambios:</p>
                  <ul className="text-xs text-muted-foreground space-y-0.5">
                    <li>• {selectedIds.size} actividad(es) seleccionada(s)</li>
                    {nuevasActividades.length > 0 && (
                      <li>• +{nuevasActividades.length} nueva(s) asignación(es)</li>
                    )}
                    {actividadesARemover.length > 0 && modo === 'replace' && (
                      <li>• -{actividadesARemover.length} desasignación(es)</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={isDisabled}>
            {isLoading ? 'Asignando...' : 'Aplicar cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
